import {
    ChangeDetectorRef,
    Component,
    computed,
    OnDestroy,
    OnInit,
    Signal,
    signal,
    ViewContainerRef,
    inject,
    effect,
    viewChild,
} from '@angular/core';
import {
    ActivatedRoute,
    Params,
    QueryParamsHandling,
    Router,
    RouterLink,
} from '@angular/router';
import {
    BehaviorSubject,
    combineLatest,
    delay,
    map,
    Observable,
    retryWhen,
    Subject,
    take,
    takeUntil,
    tap,
} from 'rxjs';
import { PaginationInstance, NgxPaginationModule } from 'ngx-pagination';
import {
    LangChangeEvent,
    TranslateService,
    TranslateModule,
} from '@ngx-translate/core';
import {
    MediaService,
    SystemDateFormatService,
    SvgViewboxDirective,
    MediaIfDirective,
    arraysEqual,
} from '@exl-ng/mulo-core';
import {
    HeightInAnimation,
    HeightOutAnimation,
    HtmlEscapePipe,
    OpacityInAnimation,
    OpacityOutAnimation,
    SearchbarAdvRootDirective,
    SideNavService,
    WidthInAnimation,
    WidthOutAnimation,
    PrologueComponent,
    MultipleEventsDirective,
    ExpandableComponent,
    SearchbarComponent,
    SearchbarOptionsComponent,
    StickyDirective,
    FloatingToolbarComponent,
    FloatingToolbarButtonsComponent,
    ToggleButtonComponent,
    FloatingToolbarSeparatorComponent,
    SeparatorComponent,
    LoadingSlateComponent,
    PagingComponent,
    StatusTagComponent,
    AvatarComponent,
    ElementAsHeadingDirective,
    HtmlSanitizePipe,
    ExportersService,
} from '@exl-ng/mulo-common';
import {
    FacetNumericalComponent,
    FacetQuery,
    CenteredBlockLayoutComponent,
    FacetsActiveComponent,
    SideMainLayoutComponent,
    FacetsComponent,
    FacetSortComponent,
    FacetSectionComponent,
    FacetItemComponent,
} from '@exl-ng/mulo-views';

import { I18nService } from '../core/i18n.service';
import { ConfigurationHandlerService } from '../core/configuration-handler.service';
import { WindowRefService } from '../core/window-ref.service';
import { Discovery } from '../shared/configurations/discovery.constant';
import { PortalSearch } from '../shared/configurations/portal-search.constant';
import { UrlUtils } from '../shared/utils/url.utils';
import {
    ActiveFacet,
    Asset,
    AssetsSearchResults,
    FacetGroupConfig,
    FacetGroupForInterface,
} from '../shared/interfaces/assets-search-results.interface';
import { Author } from '../shared/interfaces/research-portal/full-asset-page/asset-model/asset-model-relations/author.interface';
import {
    BriefResultsConfiguration,
    GeneralConfig,
} from '../shared/interfaces/research-portal/portal-configuration/brief-results-configuration.interface';
import { PortalConfiguration } from '../shared/interfaces/research-portal/portal-configuration/portal-configuration.interface';
import { ResearcherRecord } from '../shared/interfaces/researcher-record.interface';
import { ResearchersSearchResults } from '../shared/interfaces/researchers-search-results.interface';

import { ResearchUnitsService } from '../research-units/research-units.service';
import { DiscoveryService } from './discovery.service';
import { FacetService } from './facet.service';
import { DomSanitizer } from '@angular/platform-browser';
import { PortalService } from '../portal/portal.service';
import { AdvancedSearchService } from '../parts/advanced-search/advanced-search.service';
import { ResearcherProfileSearchResultsConfigurationData } from '../shared/interfaces/researcher-profile-search-results-configuration.model';
import { SEOService } from '../core/seo.service';
import { Collection } from '../shared/interfaces/collection.interface';
import { EntitySearchService } from './entity-search.service';
import { ProjectDetails } from '../shared/interfaces/project.interface';
import { ProjectsSearchResultsConfiguration } from '../shared/interfaces/projects-search-results-configuration.model';
import { DefaultTranslatePipe } from '../shared/pipes/default-translate.pipe';
import { ResearcherTitlePipe } from '../shared/pipes/researcher-title.pipe';
import { EspDateFormatPipe } from '../shared/pipes/date-format.pipe';
import { TranslateObjectPipe } from '../shared/pipes/translate-object.pipe';
import { HighlightFilterPipe } from '../shared/pipes/highlight-filter.pipe';
import { TagComponent } from '../parts/tags/tag/tag.component';
import { TagsComponent } from '../parts/tags/tags.component';
import {
    PersonBriefComponent,
    PersonBriefTitleDirective,
} from '../parts/person-brief/person-brief.component';
import { DiscoveryResultsComponent } from './discovery-results/discovery-results.component';
import { CitationBriefComponent } from '../parts/citations/citation-brief/citation-brief.component';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { CdkAriaLive } from '@angular/cdk/a11y';
import { MatTabNavPanel } from '@angular/material/tabs';
import { ResearchUnitNavComponent } from '../research-units/research-unit-nav/research-unit-nav.component';
import { AdvancedSearchComponent } from '../parts/advanced-search/advanced-search.component';
import { ResearchUnitHeaderComponent } from '../research-units/research-unit-header/research-unit-header.component';
import { MatIcon } from '@angular/material/icon';
import { MatAnchor, MatIconButton, MatButton } from '@angular/material/button';
import {
    NgTemplateOutlet,
    AsyncPipe,
    SlicePipe,
    DecimalPipe,
    DOCUMENT,
} from '@angular/common';
import {
    MatButtonToggleModule,
    MatButtonToggleChange,
} from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AssetExportService } from '../asset-page/asset-page-export/asset-export.service';

@Component({
    selector: 'esp-discovery',
    templateUrl: './discovery.component.html',
    styleUrls: ['./discovery.component.scss'],
    animations: [
        HeightInAnimation,
        HeightOutAnimation,
        WidthInAnimation,
        WidthOutAnimation,
        OpacityInAnimation,
        OpacityOutAnimation,
    ],
    imports: [
        PrologueComponent,
        CenteredBlockLayoutComponent,
        MatAnchor,
        MultipleEventsDirective,
        MatIcon,
        RouterLink,
        SvgViewboxDirective,
        ExpandableComponent,
        SearchbarAdvRootDirective,
        ResearchUnitHeaderComponent,
        SearchbarComponent,
        SearchbarOptionsComponent,
        AdvancedSearchComponent,
        ResearchUnitNavComponent,
        FacetsActiveComponent,
        MatTabNavPanel,
        StickyDirective,
        FloatingToolbarComponent,
        CdkAriaLive,
        FloatingToolbarButtonsComponent,
        MediaIfDirective,
        ToggleButtonComponent,
        FloatingToolbarSeparatorComponent,
        MatIconButton,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        SeparatorComponent,
        LoadingSlateComponent,
        SideMainLayoutComponent,
        NgTemplateOutlet,
        NgxPaginationModule,
        PagingComponent,
        FacetsComponent,
        FacetSortComponent,
        FacetSectionComponent,
        MatButton,
        FacetItemComponent,
        FacetNumericalComponent,
        CitationBriefComponent,
        StatusTagComponent,
        DiscoveryResultsComponent,
        PersonBriefComponent,
        AvatarComponent,
        ElementAsHeadingDirective,
        PersonBriefTitleDirective,
        TagsComponent,
        TagComponent,
        SlicePipe,
        DecimalPipe,
        TranslateModule,
        HighlightFilterPipe,
        TranslateObjectPipe,
        EspDateFormatPipe,
        ResearcherTitlePipe,
        DefaultTranslatePipe,
        HtmlEscapePipe,
        HtmlSanitizePipe,
        MatButtonToggleModule,
        MatCheckboxModule,
    ],
})
export class DiscoveryComponent implements OnInit, OnDestroy {
    discoveryService = inject(DiscoveryService);
    private route = inject(ActivatedRoute);
    router = inject(Router);
    media = inject(MediaService);
    private translate = inject(TranslateService);
    private facetService = inject(FacetService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private windowRef = inject(WindowRefService);
    private i18nService = inject(I18nService);
    private researchUnitsService = inject(ResearchUnitsService);
    private sidenavService = inject(SideNavService);
    private sanitizer = inject(DomSanitizer);
    private cdr = inject(ChangeDetectorRef);
    private portalService = inject(PortalService);
    private advancedSearchService = inject(AdvancedSearchService);
    private seoService = inject(SEOService);
    private entitySearchService = inject(EntitySearchService);
    private systemDateFormatService = inject(SystemDateFormatService);
    private document = inject<Document>(DOCUMENT);
    _window = this.document.defaultView;
    private assetExportSvc = inject(AssetExportService);
    private exportersService = inject(ExportersService);

    institution = UrlUtils.getParam('institution');
    input: string;
    results = [];

    toolbarScrolled;
    toggleButtonState: 'left' | 'right' = 'left';

    searchScope = '';

    toolbarStuck = false;
    toolbarSticky = false;
    searchInProgress = false;
    noResults = false;
    resultsAfterAllScope = false;

    focusOnSearchbar = false;

    maxVisibleTags = 8;

    paginationConfig = signal<PaginationInstance>({
        id: 'serverPaging',
        itemsPerPage: Discovery.ASSETS_BULK_SIZE,
        currentPage: 1,
    });
    pagesCount = computed<number>(() => {
        const config = this.paginationConfig();
        const totalItems = config.totalItems ?? 0;
        const itemsPerPage = config.itemsPerPage ?? 1;
        return Math.ceil(totalItems / itemsPerPage);
    });
    resultCountOptions = signal([10, 20, 30]);

    showCheckboxes = true;
    routeParams: Params;

    assets = signal<Asset[]>([]);
    allAssets = signal<Asset[]>([]);
    researchers: ResearcherRecord[];
    projects: ProjectDetails[] = [];
    facetsForInterface: FacetGroupForInterface[] = [];

    searchQuery: string;
    searchQueryTemp: string;

    searchEntities = [];

    activeEntity = signal('outputs');
    selectedEntity: string;
    searchScopesByEntity = [];
    activeScope: string;
    selectedScope: string;
    searchbarPlaceholder = 'research.search.scope.outputs.placeholder';
    activeFilters: ActiveFacet[];
    showViewToggle: string;
    showFacets = false;
    autocompleteSearchBy = [];
    state: string;
    stateMessage = '';

    sortOptions = [];
    selectedSort = 'rank';

    briefResults: BriefResultsConfiguration;
    facetsConfigurationMappingByFacetName = new Map();
    general: GeneralConfig;

    projectsSearchResultsConfiguration: ProjectsSearchResultsConfiguration;
    projectSearchFacetsConfigurationMappingByFacetName = new Map();

    profileSearchFacetsConfiguration: ResearcherProfileSearchResultsConfigurationData;
    profileSearchFacetsConfigurationMappingByFacetName = new Map();

    searchHeader: string;
    searchHeaderPalette: 'default' | undefined = undefined;
    breadcrumbsArray = [];
    firstLevelSubUnits = [];
    showSearchbar = true;
    showUnitSearchbar = false;
    displayAltmetric: boolean;

    advancedSearchEnabled = false;
    openAdvancedSearchStart = false;

    isProfileUnitTabEnabled = false;
    orgUnitSections = [];

    isCollection = false;
    collectionCode: string;
    collectionIncludeSearchFacets = true;
    headerImage;

    componentDestroy = new Subject<void>();
    useAltOAIcon: Signal<boolean>;

    readonly creationDateFacet =
        viewChild<FacetNumericalComponent>('creationDateFacet');
    readonly facetsBlock = viewChild<ViewContainerRef>('facetsBlock');
    readonly advSearchRootElem =
        viewChild<SearchbarAdvRootDirective>('advSearchRoot');

    checkedItems = signal<any[]>([], {
        equal: arraysEqual,
    });
    pageItemsAllChecked: Signal<boolean>;
    pageItemsSomeChecked: Signal<boolean>;

    constructor() {
        this.displayAltmetric =
            this.configurationHandlerService.briefDisplayAltmetrics();
        this.useAltOAIcon = computed(
            () =>
                this.configurationHandlerService.config().customerParameters?.[
                    'esploro_alt_open_access_icon'
                ] === 'true',
        );

        this.pageItemsAllChecked = computed(() => {
            const assets = this.assets();
            const checkedItems = this.checkedItems();

            return assets.every((asset) =>
                this.isAssetChecked(asset, checkedItems),
            );
        });
        this.pageItemsSomeChecked = computed(() => {
            const assets = this.assets();
            const checkedItems = this.checkedItems();

            return (
                assets.some((asset) =>
                    this.isAssetChecked(asset, checkedItems),
                ) &&
                !assets.every((asset) =>
                    this.isAssetChecked(asset, checkedItems),
                )
            );
        });
    }

    isAssetChecked(asset: Asset, checkedItems?: any[]) {
        checkedItems = checkedItems ?? this.checkedItems();
        return checkedItems.some((i) => i.id === asset.id);
    }

    ngOnInit() {
        this.searchEntities = this.entitySearchService.getSearchEntities();
        this.isProfileUnitTabEnabled =
            this.researchUnitsService.isProfileUnitTabEnabled();
        this.orgUnitSections = this.researchUnitsService.configSections;
        this.initBriefResultsConfiguration();
        this.initProfileSearchConfiguration();
        this.initProjectsSearchConfiguration();
        this.reloadAssetAfterLanguageChange();
        this.showUnitSearchbar =
            this.configurationHandlerService.portalOrgTreeConfig()?.generalConfiguration?.displayUnitSearch;

        this.route.queryParams
            .pipe(takeUntil(this.componentDestroy))
            .subscribe((queryParams) => {
                const advSearchOpen = queryParams.mode === 'advanced';
                if (this.openAdvancedSearchStart && !advSearchOpen) {
                    this.advSearchRootElem()?.closeAdvSearch();
                }
                if (this.media.is('lt-md')) {
                    if (queryParams.query?.length > 0) {
                        this.sidenavService.close('secondary');
                    } else {
                        this.portalService.openMobileAdvSearch.next();
                    }
                }

                const path = this.route.firstChild?.routeConfig?.path;
                if (path?.startsWith('collections')) {
                    this.isCollection = true;
                    this.activeEntity.set(
                        this.route.snapshot.children[0].url.join('/'),
                    );
                    this.selectedEntity = this.activeEntity();
                    this.setSortOptions('outputs');
                    this.seoService.createLinkForCanonicalURL();
                } else {
                    this.isCollection = false;
                    this.collectionCode = undefined;
                    this.setCurrentEntity(path);
                    this.setCurrentScope(
                        queryParams.scope &&
                            this.isScopeValid(queryParams.scope)
                            ? queryParams.scope
                            : this.getDefaultScope(path),
                    );
                    this.setSortOptions(path);
                }

                if (this.isValidSearch(queryParams)) {
                    if (this.isResearchUnitSearch(queryParams)) {
                        this.handleUnitSearch(path, queryParams.unit);
                    } else if (this.isCollection) {
                        this.handleCollectionSearch();
                    } else {
                        this.handleSearch();
                    }
                    this.performSearch(path, queryParams);
                }
                this.openAdvancedSearchStart = advSearchOpen;
                // load the facets block template into the sidenav panel
                if (this.media.is('lt-md') && queryParams.query?.length > 0) {
                    this.sidenavService.close('secondary', this.facetsBlock(), {
                        position: 'end',
                    });
                }
                this.updateTitle();
                this.cdr.detectChanges();
            });
    }

    isValidSearch(queryParams) {
        return (
            queryParams.query ||
            this.isResearchUnitSearch(queryParams) ||
            this.isCollection
        );
    }

    handleSearch() {
        this.showSearchbar = true;
        this.searchHeader = undefined;
        this.searchHeaderPalette = undefined;
    }

    updateTitle() {
        if (this.isCollection) {
            this.translate
                .get([
                    'research.search.collections',
                    'research.collection.' + this.collectionCode + '.name',
                ])
                .subscribe((title) => {
                    this.seoService.updatePageTitle(
                        Object.values(title).join('- '),
                    );
                });
        } else {
            const titleCode = this.searchHeader
                ? ['research.search.academic.units', this.searchHeader]
                : 'research.profiles.search.document.title';
            this.translate.get(titleCode).subscribe((title) => {
                const translatedTitle =
                    typeof title === 'string'
                        ? title
                        : Object.values(title).join('- ');
                this.seoService.updatePageTitle(translatedTitle);
            });
        }
    }

    handleCollectionSearch() {
        this.collectionCode =
            this.route.snapshot.children[0].params['collection-code'];
        this.showSearchbar = false;
        this.isProfileUnitTabEnabled = false;
        this.searchHeaderPalette = 'default';
        this.loadCollection();
    }

    loadCollection() {
        this.discoveryService
            .getCollectionByCode(this.collectionCode)
            .subscribe((data) => {
                if (data) {
                    const collection = data as Collection;
                    this.searchHeader = collection.code;
                    this.headerImage = collection.image;
                    this.collectionIncludeSearchFacets =
                        collection.includeSearchFacets;
                    this.showSearchbar = collection.includeSearchBar;
                }
            });
    }

    handleUnitSearch(path, unit) {
        this.showSearchbar = false;
        this.searchHeader = unit;
        this.searchHeaderPalette = 'default';
        this.loadUnitData(path, unit);
    }

    isResearchUnitSearch(queryParams) {
        return queryParams.unit;
    }

    loadUnitData(path, unit) {
        this.loadOrganizationAncestors(unit);
        this.loadOrganizationFirstLevelSubUnits(path, unit);
    }

    loadOrganizationFirstLevelSubUnits(path, unit) {
        this.researchUnitsService
            .getOrgFirstLevelSubUnits(unit)
            .subscribe((data) => {
                this.firstLevelSubUnits = data ? (data as String[]) : [];
                this.sortSubUnitsByNameWhenTranslationsAreReady();
            });
    }

    loadOrganizationAncestors(unit) {
        this.researchUnitsService.getOrgAncestors(unit).subscribe((data) => {
            this.breadcrumbsArray = data ? (data as String[]) : [];
            this.breadcrumbsArray.reverse();
            this.breadcrumbsArray.shift();
            this.breadcrumbsArray.pop();
        });
    }

    sortSubUnitsByNameWhenTranslationsAreReady() {
        this.translate.get('key').subscribe((data) => {
            this.firstLevelSubUnits.sort((a, b) =>
                this.researchUnitsService.alphabeticallySortAfterTranslation(
                    a,
                    b,
                ),
            );
        });
    }

    reloadAssetAfterLanguageChange() {
        this.translate.onLangChange
            .pipe(takeUntil(this.componentDestroy))
            .subscribe((event: LangChangeEvent) => {
                if (this.assetsSearch || this.projectsSearch) {
                    console.info(
                        'changing language, reload assets search in ' +
                            this.i18nService.getLanguage(),
                    );
                    this.setRoute(
                        { lang: this.i18nService.getLanguage() },
                        this.selectedEntity,
                        'merge',
                    );
                }
            });
    }

    initBriefResultsConfiguration() {
        const configuration =
            this.configurationHandlerService.portalConfigurationByDefaultAssetType() as PortalConfiguration;
        this.briefResults = configuration?.briefResults;
        const facetsConfiguration = this.briefResults?.facets || [];
        this.facetsConfigurationMappingByFacetName =
            this.facetService.buildFacetConfiguration(facetsConfiguration);
        // this.initSortOptions(this.briefResults.sort);
    }

    initProfileSearchConfiguration() {
        this.profileSearchFacetsConfiguration =
            this.configurationHandlerService.researcherProfileSearchResultsConfiguration() as ResearcherProfileSearchResultsConfigurationData;
        const facetsConfiguration =
            this.profileSearchFacetsConfiguration?.facets || [];
        this.profileSearchFacetsConfigurationMappingByFacetName =
            this.facetService.buildFacetConfiguration(facetsConfiguration);
    }

    initProjectsSearchConfiguration() {
        this.projectsSearchResultsConfiguration =
            this.configurationHandlerService.portalProjectSearchConfiguration();
        const facetsConfiguration =
            this.projectsSearchResultsConfiguration?.facets || [];
        this.projectSearchFacetsConfigurationMappingByFacetName =
            this.facetService.buildFacetConfiguration(facetsConfiguration);
        // this.initSortOptions(this.projectsSearchResultsConfiguration.sort);
    }

    performSearch(path, queryParams) {
        this.routeParams = queryParams;
        this.clearStateMessage();
        if (path === 'researchers') {
            this.searchResearchers(queryParams);
        } else if (path === 'outputs' || this.isCollection) {
            this.searchAssets(queryParams);
        } else if (path === 'projects') {
            this.searchProjects(queryParams);
        }
    }

    onSearchSubmit(value) {
        if (!value.query && this.assetsSearch) {
            this.setAssetNoQueryState();
        } else if (value.query) {
            if (this.assetsSearch) {
                value.query =
                    Discovery.SIMPLE_SEARCH_QUERY_PREFIX + value.query;
            }
            const queryParams = this.getSearchQueryParam(value);
            if (this.researchersSearch) {
                this.setRoute(queryParams, 'researchers');
            } else if (this.isCollection) {
                queryParams.enable_asterisk_search = 'true';
                this.setRoute(
                    queryParams,
                    'collections/' + this.collectionCode,
                );
            } else if (this.assetsSearch) {
                this.setRoute(queryParams, 'outputs');
            } else if (this.projectsSearch) {
                this.setRoute(queryParams, 'projects');
            }
        }
    }

    setAssetNoQueryState() {
        this.setState(
            'ASSETS_SEARCH',
            'research.search.results.assets.noQuery',
        );
        this.clearUI();
    }

    clearUI() {
        this.researchers = [];
        this.assets.set([]);
        this.showFacets = false;
    }

    clearStateMessage() {
        this.state = '';
        this.stateMessage = '';
    }

    setSortOptions(entity) {
        let sortConfiguration;
        if (entity === 'outputs') {
            sortConfiguration = this.briefResults.sort;
        } else if (entity === 'projects') {
            sortConfiguration = this.projectsSearchResultsConfiguration?.sort;
        } else {
            return;
        }
        const sortMethods = sortConfiguration.filter((method) => method.active);
        if (sortMethods.length > 0) {
            this.selectedSort = sortMethods[0].code;
            const sortOptionsCode = sortMethods.map((method) => {
                return Discovery.SORT_CODE_PREFIX + method.code;
            });
            this.translateSort(sortOptionsCode);
        }
    }

    translateSort(sortOptionsCode) {
        this.sortOptions = [];
        //   const sort = [];
        if (sortOptionsCode.length > 0) {
            this.translate.get(sortOptionsCode).subscribe((res) => {
                for (const key of Object.keys(res)) {
                    const sort = {
                        id: key.replace(Discovery.SORT_CODE_PREFIX, ''),
                        value: res[key],
                    };
                    this.sortOptions.push(sort);
                    //this.outputsSort.push(sort);
                    //  sortList.push(sort);
                }
            });
        }
    }

    getSearchQueryParam(value) {
        const queryParams = { ...this.routeParams, ...value };
        queryParams['page'] = 1;
        queryParams['institution'] = this.institution;
        queryParams['scope'] = this.selectedScope
            ? this.selectedScope
            : this.getDefaultScope(this.selectedEntity);
        if (this.assetsSearch || this.projectsSearch) {
            queryParams['sort'] = this.selectedSort;
        }
        return queryParams;
    }

    getDefaultScope(entity) {
        if (entity === 'researchers' || entity === 'projects') {
            return this.searchScopesByEntity[0]?.value || '';
        } else if (entity === 'outputs' || this.isCollection) {
            return PortalSearch.OUTPUTS_DEFAULT_SCOPE;
        }
        return '';
    }

    get researchersSearch() {
        return this.selectedEntity === 'researchers';
    }

    get assetsSearch() {
        return this.selectedEntity === 'outputs' || this.isCollection;
    }

    get projectsSearch() {
        return this.selectedEntity === 'projects';
    }

    searchAssets(params, firstTry = true) {
        this.researchers = [];
        this.projects = [];
        this.searchScope = undefined;

        let query = params.query ? params.query : '';
        const mode = params.mode;
        this.setQueryForSearchBar(query, mode);
        const pfilter = params.pfilter;
        if (pfilter) {
            query = this.getQueryWithPfilters(pfilter, query);
        }
        if (Array.isArray(query)) {
            query = this.getQueryAsString(query);
        } else if (query) {
            query = this.replaceSemicolon(query);
        }
        if (this.isCollection && this.collectionCode) {
            const esploroAssetCollectionExact =
                'esploro_asset_collection,exact,' + this.collectionCode;
            query = query
                ? query + ',AND;' + esploroAssetCollectionExact
                : esploroAssetCollectionExact;
        }
        if (params.sort) {
            this.selectedSort = this.getSortByParamForSearch(
                params.sort,
                'rank',
            );
        }
        const scope = params.scope
            ? params.scope
            : PortalSearch.OUTPUTS_DEFAULT_SCOPE;
        const page = params.page ? params.page : 1;
        this.paginationConfig.update((cfg) => ({ ...cfg, currentPage: page }));

        const offset = this.searchOffset();
        const facetArray = params.facet;
        const qInclude = facetArray
            ? this.facetService.processFacetsInclude(facetArray)
            : '';
        const qExclude = facetArray
            ? this.facetService.processFacetsExclude(facetArray)
            : '';
        const mfacet = params.mfacet ? params.mfacet : [];
        const mfacetByGroups = this.facetService.processMultiFacet(mfacet);
        const enableAsteriskSearch =
            params.enable_asterisk_search &&
            params.enable_asterisk_search === 'true';
        const unit = params.unit;

        this.processActiveFacetsWhenTranslationIsReady(
            facetArray ? facetArray : [],
            mfacet,
        );

        this.searchInProgress = true;
        this.discoveryService
            .getAssets(
                query,
                scope,
                qInclude,
                qExclude,
                mfacetByGroups,
                offset,
                this.selectedSort,
                enableAsteriskSearch,
                unit,
                this.paginationConfig().itemsPerPage,
            )
            .subscribe({
                next: (assetSearchResults) => {
                    this.searchInProgress = false;
                    const totalItems = assetSearchResults.info.total;
                    this.paginationConfig.update((cfg) => ({
                        ...cfg,
                        totalItems,
                    }));
                    this.assets.set(assetSearchResults.assets);
                    const facets = assetSearchResults.facets;

                    if (
                        totalItems > 1 &&
                        (!this.isCollection ||
                            (this.isCollection &&
                                this.collectionIncludeSearchFacets))
                    ) {
                        this.showFacets = true;
                        this.processFacetsWhenTranslationIsReady(
                            facets,
                            'outputs',
                            this.facetsConfigurationMappingByFacetName,
                        );
                        if (facets.length === 0) {
                            this.retryFacets(
                                query,
                                scope,
                                qInclude,
                                qExclude,
                                mfacetByGroups,
                                offset,
                                this.selectedSort,
                                enableAsteriskSearch,
                                unit,
                            );
                        }
                    } else {
                        this.showFacets = false;
                        this.facetsForInterface = [];
                        if (totalItems === 0) {
                            if (firstTry) {
                                this.searchAssets(params, false);
                            } else if (this.searchQuery) {
                                this.setState(
                                    'ASSETS_SEARCH',
                                    'research.search.results.assets.noResults',
                                    this.searchQuery,
                                );
                            } else {
                                this.setState(
                                    'ASSETS_SEARCH',
                                    'research.search.results.assets.error',
                                );
                            }
                        }
                    }
                },
                error: () => {
                    if (firstTry) {
                        this.searchAssets(params, false);
                    } else {
                        this.onAssetSearchError();
                    }
                },
            });
    }

    getSortByParamForSearch(sort, fallback) {
        let selectedSort;
        if (
            this.sortOptions.filter((option) => option.id === sort).length > 0
        ) {
            selectedSort = sort;
        } else {
            selectedSort =
                this.sortOptions.length > 0 ? this.sortOptions[0].id : fallback;
        }
        return selectedSort;
    }

    getQueryAsString(query) {
        for (let i = 0; i < query.length; i++) {
            query[i] = this.replaceSemicolon(query[i]);
        }
        query = query.join(';');
        return query;
    }

    //Causes problem in server, must replace it with a space
    replaceSemicolon(str) {
        return str?.replaceAll(';', ' ');
    }

    getQueryWithPfilters(pfilter, query) {
        if (!Array.isArray(pfilter)) {
            pfilter = [pfilter];
        }
        if (!Array.isArray(query)) {
            query = [query];
        }
        query = query.concat(pfilter);
        return query;
    }

    setQueryForSearchBar(query, mode) {
        if (query && !mode) {
            this.setSimpleQueryForSearchBar(query);
        } else if (query && mode === 'advanced') {
            this.setAdvancedQueryForSearchBar(query);
        }
    }

    setSimpleQueryForSearchBar(query: string) {
        this.searchQuery = this.normalizeQueryForSearchBar(query);
        this.searchQueryTemp = this.searchQuery;
    }

    setAdvancedQueryForSearchBar(query: string) {
        let advancedQuery = query;
        if (Array.isArray(query)) {
            advancedQuery = query[0];
        }
        const elements = advancedQuery.split(',');
        if (elements.length >= 3) {
            const searchIndex =
                'research.portal.search.index_names.' + elements[0];
            const precisionOperator =
                'research.advanced.search.precisionOperator.option.' +
                elements[1];
            this.translate
                .get([searchIndex, precisionOperator])
                .subscribe((data) => {
                    const searchTerm =
                        this.advancedSearchService.getSearchTerm(elements);
                    this.searchQuery =
                        data[searchIndex] +
                        ' ' +
                        data[precisionOperator] +
                        ' ' +
                        searchTerm +
                        '...';
                });
        }
    }

    normalizeQueryForSearchBar(query: string) {
        const searchBarText = query.replace(
            Discovery.SIMPLE_SEARCH_QUERY_PREFIX,
            '',
        );
        if (searchBarText === '*') {
            return '';
        }
        return searchBarText;
    }

    onAssetSearchError() {
        this.searchInProgress = false;
        this.setState('ASSETS_SEARCH', 'research.search.results.assets.error');
        this.clearUI();
    }

    processActiveFacetsWhenTranslationIsReady(facetArray, mfacet) {
        this.translate.get('key').subscribe((data) => {
            this.activeFilters = this.facetService.processActiveFacets(
                facetArray,
                mfacet,
            );
        });
    }

    processFacetsWhenTranslationIsReady(
        facets,
        entity,
        facetsConfiguration: Map<string, FacetGroupConfig>,
    ) {
        this.translate.get('key').subscribe((data) => {
            const activeFacets = this.routeParams.facet
                ? this.facetService.getFacetsAsArray(this.routeParams.facet)
                : [];
            const activeMultiFacet = this.routeParams.mfacet
                ? this.facetService.getFacetsAsArray(this.routeParams.mfacet)
                : [];
            this.facetsForInterface = this.facetService.buildFacetsForInterface(
                facets,
                activeFacets.concat(activeMultiFacet),
                facetsConfiguration,
                entity,
            );
        });
    }

    retryFacets(
        query,
        scope,
        qInclude,
        qExclude,
        mfacetByGroups,
        offset,
        sort,
        enableAsteriskSearch,
        unit,
    ) {
        this.discoveryService
            .getFacets(
                query,
                scope,
                qInclude,
                qExclude,
                mfacetByGroups,
                offset,
                sort,
                enableAsteriskSearch,
                unit,
            )
            .pipe(
                map((response) => {
                    const assetSearchResults = response as AssetsSearchResults;
                    if (assetSearchResults.facets.length === 0) {
                        throw new Error();
                    }
                    return response;
                }),
                retryWhen((errors) =>
                    errors.pipe(
                        take(Discovery.FACETS_CALL_RETRIES),
                        delay(Discovery.FACETS_CALL_DELAY),
                    ),
                ),
            )
            .subscribe((data) => {
                const assetSearchResults = data as AssetsSearchResults;
                this.processFacetsWhenTranslationIsReady(
                    assetSearchResults.facets,
                    'outputs',
                    this.facetsConfigurationMappingByFacetName,
                );
            });
    }

    searchResearchers(event, firstTry = true) {
        this.assets.set([]);
        this.projects = [];
        this.activeFilters = [];
        this.focusOnSearchbar = true;

        const searchTerm = event.query;
        const scope =
            event.scope && this.isScopeValid(event.scope)
                ? event.scope
                : this.searchScopesByEntity[0]?.value || '';
        const page = event.page ? event.page : 1;
        const unit = event.unit ? event.unit : '';

        this.searchInProgress = true;
        this.noResults = false;
        this.paginationConfig.update((cfg) => ({ ...cfg, currentPage: page }));

        this.searchQuery = searchTerm === '*' ? '' : searchTerm;
        this.searchQueryTemp = this.searchQuery;
        this.searchScope = scope;

        const facetArray = event.facet;
        const qInclude = facetArray
            ? this.facetService.processFacetsInclude(facetArray)
            : '';
        const qExclude = facetArray
            ? this.facetService.processFacetsExclude(facetArray)
            : '';
        const mfacet = event.mfacet ? event.mfacet : [];
        const mfacetByGroups = this.facetService.processMultiFacet(mfacet);

        this.processActiveFacetsWhenTranslationIsReady(
            facetArray ? facetArray : [],
            mfacet,
        );

        const observable = this.discoveryService.getResearchersBySearchIndex(
            searchTerm,
            this.searchOffset(),
            scope,
            qInclude,
            qExclude,
            mfacetByGroups,
            unit,
            this.paginationConfig().itemsPerPage,
        );

        observable.subscribe({
            next: (data) => {
                const searchResults = data as ResearchersSearchResults;
                this.researchers = searchResults.records;
                const facets = searchResults.facets;
                const totalItems = searchResults.totalResults;
                this.paginationConfig.update((cfg) => ({
                    ...cfg,
                    totalItems,
                }));
                this.resultsAfterAllScope = false;

                if (totalItems > 1 && facets.length > 0) {
                    this.showFacets = true;
                    this.processFacetsWhenTranslationIsReady(
                        facets,
                        'researchers',
                        this.profileSearchFacetsConfigurationMappingByFacetName,
                    );
                } else {
                    this.showFacets = false;
                    this.facetsForInterface = [];
                    if (totalItems === 0) {
                        if (firstTry) {
                            this.searchResearchers(event, false);
                        } else {
                            this.noResults = true;
                            if (this.searchQuery) {
                                this.setState(
                                    'RESEARCHERS_SEARCH',
                                    'research.search.results.researchers.noResults',
                                    this.searchQuery,
                                );
                            } else {
                                this.setState(
                                    'RESEARCHERS_SEARCH',
                                    'research.search.results.assets.error',
                                );
                            }
                        }
                    }
                }
                this.disableProgressBar();
            },
            error: () => {
                if (firstTry) {
                    this.searchResearchers(event, false);
                } else {
                    this.disableProgressBar();
                    this.noResults = true;
                }
            },
            complete: () => {
                this.focusOnSearchbar = false;
            },
        });
    }

    searchProjects(event, firstTry = true) {
        this.assets.set([]);
        this.researchers = [];
        this.activeFilters = [];
        this.focusOnSearchbar = true;

        const searchTerm = event.query;
        const scope =
            event.scope && this.isScopeValid(event.scope)
                ? event.scope
                : this.searchScopesByEntity[0]?.value || '';
        const page = event.page ? event.page : 1;
        const unit = event.unit ? event.unit : '';
        if (event.sort) {
            this.selectedSort = this.getSortByParamForSearch(
                event.sort,
                'date_d',
            );
        }
        this.searchInProgress = true;
        this.noResults = false;
        this.paginationConfig.update((cfg) => ({ ...cfg, currentPage: page }));
        this.searchQuery = searchTerm === '*' ? '' : searchTerm;
        this.searchQueryTemp = this.searchQuery;
        this.searchScope = undefined;

        const facetArray = event.facet;
        const qInclude = facetArray
            ? this.facetService.processFacetsInclude(facetArray)
            : '';
        const qExclude = facetArray
            ? this.facetService.processFacetsExclude(facetArray)
            : '';
        const mfacet = event.mfacet ? event.mfacet : [];
        const mfacetByGroups = this.facetService.processMultiFacet(mfacet);

        this.processActiveFacetsWhenTranslationIsReady(
            facetArray ? facetArray : [],
            mfacet,
        );

        this.discoveryService
            .getProjects(
                scope,
                searchTerm,
                qInclude,
                qExclude,
                mfacetByGroups,
                this.searchOffset(),
                this.selectedSort,
                unit,
                this.paginationConfig().itemsPerPage,
            )
            .subscribe({
                next: (data) => {
                    this.projects = data.results;
                    const facets = data.facets;
                    const totalItems = data.count;
                    this.paginationConfig.update((cfg) => ({
                        ...cfg,
                        totalItems,
                    }));

                    if (totalItems > 1 && facets.length > 0) {
                        this.showFacets = true;
                        this.processFacetsWhenTranslationIsReady(
                            facets,
                            'projects',
                            this
                                .projectSearchFacetsConfigurationMappingByFacetName,
                        );
                    } else {
                        this.showFacets = false;
                        this.facetsForInterface = [];
                        if (totalItems === 0) {
                            if (firstTry) {
                                this.searchProjects(event, false);
                            } else {
                                this.noResults = true;
                                if (this.searchQuery) {
                                    this.setState(
                                        'ASSETS_SEARCH',
                                        'research.search.results.assets.noResults',
                                        this.searchQuery,
                                    );
                                } else {
                                    this.setState(
                                        'ASSETS_SEARCH',
                                        'research.search.results.assets.error',
                                    );
                                }
                            }
                        }
                    }
                    this.disableProgressBar();
                },
                error: () => {
                    if (firstTry) {
                        this.searchProjects(event, false);
                    } else {
                        this.disableProgressBar();
                        this.noResults = true;
                    }
                },
                complete: () => {
                    this.focusOnSearchbar = false;
                },
            });
    }

    setState(state, stateMessage, param = '') {
        this.state = state;
        let interpolateParams;
        if (param) {
            interpolateParams = { value: HtmlEscapePipe.escape(param) };
        }
        this.translate
            .get(stateMessage, interpolateParams)
            .subscribe((data) => {
                this.stateMessage = data;
            });
    }

    searchAgainByAll() {
        this.setRoute({ scope: 'all' }, 'researchers', 'merge');
    }

    disableProgressBar() {
        setTimeout(() => {
            this.searchInProgress = false;
        }, 500);
    }

    searchQueryWithoutLastAsterisk(searchQuery) {
        if (searchQuery?.indexOf('*') === searchQuery?.length - 1) {
            return searchQuery.substring(0, searchQuery.length - 1);
        }
        return searchQuery;
    }

    get view() {
        return this.toggleButtonState === 'left' ? 'tiles' : 'list';
    }

    get viewAsTiles() {
        return this.view === 'tiles' && !this.media.is('xs');
    }

    getAriaLabelForToggle() {
        if (this.toggleButtonState === 'left') {
            return 'research.aria.toggle.list.view';
        }
        return 'research.aria.toggle.tile.view';
    }

    onPersonClick(id) {
        this.router.navigate(['../profile/' + id], {
            queryParams: { institution: this.institution },
        });
    }

    get resultsByAffiliation() {
        return this.searchScope === 'affiliation';
    }

    get resultsByName() {
        return this.searchScope === 'name';
    }

    searchOffset = computed(() => {
        const cfg = this.paginationConfig();
        return (cfg.currentPage - 1) * cfg.itemsPerPage;
    });

    currentPageStart = computed(() => this.searchOffset() + 1);

    currentPageEnd = computed(() => {
        const cfg = this.paginationConfig();
        return Math.min(cfg.totalItems, cfg.currentPage * cfg.itemsPerPage);
    });

    pageChange(page: number) {
        this.resultsAfterAllScope = false;
        this.paginationConfig.update((cfg) => ({ ...cfg, currentPage: page }));
        this.setRoute({ page }, this.selectedEntity, 'merge');
    }

    totalResults = computed(() => this.paginationConfig().totalItems);

    changeToggle(event) {
        this.toggleButtonState = event;
    }

    handleSortChange(value) {
        this.setRoute({ sort: value, page: 1 }, this.selectedEntity, 'merge');
    }

    resultSelected(event) {
        this.noResults = false;
        this.resultsAfterAllScope = false;
        const result = event.result as ResearcherRecord;
        this.paginationConfig.update((cfg) => ({
            ...cfg,
            currentPage: 1,
            totalItems: 1,
        }));
        this.researchers = [result];
    }

    getLines(asset: Asset) {
        const lines = [];
        lines.push(asset.publicationDetails);
        lines.push(asset.conferenceDetails);
        lines.push(asset.eventDetails);
        lines.push(asset.etdDetails);
        lines.push(asset.seriesDetails);
        lines.push(asset.date);
        lines.push(asset.identifiers);
        return lines.filter((line) => line);
    }

    getAuthors(creators: Author[]) {
        return creators?.map((creator) => {
            return creator.displayName;
        });
    }

    setRoute(queryParams, path, queryParamsHandling: QueryParamsHandling = '') {
        this.sidenavService.close('secondary');
        this.router.navigate(['./' + path], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling,
        });
    }

    handleClearSelection(section, index) {
        if (
            section.key === Discovery.CREATION_DATE_FACET ||
            section.key === Discovery.INTERNAL_AFFILIATION_START_DATE_FACET
        ) {
            this.creationDateFacet().reset();
        }

        section.showSectionActions = false;
        section.items.forEach((item) => {
            item.checked = false;
            section.showSectionActions = false;
            this.facetsForInterface.forEach((sec, i) => {
                sec.selectable = true;
            });
        });
    }

    handleApplySelection(section) {
        if (
            section.key === Discovery.CREATION_DATE_FACET ||
            section.key === Discovery.INTERNAL_AFFILIATION_START_DATE_FACET
        ) {
            this.handleDateFacetSubmit(
                this.creationDateFacet().currentValues,
                section,
            );
            return;
        }

        const items = section.items.filter((item) => item.checked);
        const group = this.getMultiFacetIndex();
        items.forEach((item) => {
            item.multiFacetGroupId = group;
        });
        const facets = items.map(
            (item) =>
                `${section.key},${item.excluded ? 'exclude' : 'include'},${
                    item.key
                },${group}`,
        );
        const query = this.addMultiFacetToQuery(facets);
        this.setRoute(query, this.selectedEntity);
    }

    handleSectionReveal(section) {
        section.revealed = !section.revealed;
        this.cdr.detectChanges();
    }

    handleExcluded(event, item?, section?) {
        if (item.checked) {
            item.excluded = !item.excluded;
        } else {
            item.excluded = true;
            const facet = `${section.key},exclude,${item.key}`;
            const query = this.addFacetToQuery(facet);
            this.setRoute(query, this.selectedEntity);
        }
    }

    handleChecked(value, item, section, sectionIndex?) {
        item.checked = value;
        if (!value) {
            item.excluded = false;
        }
        const selectedLength = section.items.filter((i) => i.checked).length;
        section.showSectionActions = selectedLength > 0;

        /* Disable checking within other sections to prevent zero-results (AND operator) */
        this.facetsForInterface.forEach((sec, i) => {
            sec.selectable = selectedLength ? i === sectionIndex : true;
        });
    }

    isScopeValid(scopeName: string): boolean {
        return (
            this.searchScopesByEntity.filter(
                (scope) => scope.value === scopeName,
            ).length > 0
        );
    }

    addFacetToQuery(facet) {
        const newFacet = () => {
            if (Array.isArray(this.routeParams.facet)) {
                return { facet: [...this.routeParams.facet, facet] };
            } else {
                return { facet: [this.routeParams.facet, facet] };
            }
        };

        return {
            ...this.routeParams,
            ...{ page: 1 },
            ...(this.routeParams.facet ? { ...newFacet() } : { facet }),
        };
    }

    addMultiFacetToQuery(mfacet) {
        const newFacet = () => {
            if (Array.isArray(this.routeParams.mfacet)) {
                return { mfacet: this.routeParams.mfacet.concat(mfacet) };
            } else {
                return { mfacet: [this.routeParams.mfacet].concat(mfacet) };
            }
        };

        return {
            ...this.routeParams,
            ...{ page: 1 },
            ...(this.routeParams.mfacet ? { ...newFacet() } : { mfacet }),
        };
    }

    handleFacetClick(event, item?, section?) {
        item.excluded = false;
        const facet = `${section.key},include,${item.key}`;
        const query = this.addFacetToQuery(facet);
        this.setRoute(query, this.selectedEntity);
    }

    getMultiFacetIndex() {
        const mfacet = this.routeParams.mfacet;
        if (mfacet && Array.isArray(mfacet)) {
            const last = mfacet[mfacet.length - 1];
            const index = last.lastIndexOf(',');
            if (index !== -1 && last.length >= index + 2) {
                return parseInt(last.charAt(index + 1)) + 1;
            }
        }
        return 1;
    }

    handleEntityChange(entity) {
        this.selectedEntity = entity;
        this.advancedSearchEnabled =
            entity === 'outputs' &&
            this.configurationHandlerService.advancedSearchConfiguration()
                .enableAdvancedSearch;
        this.setSearchScopesByEntity(entity);
        this.setSearchbarPlaceholder(entity);
    }

    handleScopeChange(scope) {
        this.selectedScope = scope;
    }

    handleQueryChange(input: string, isUnitSearch = false) {
        this.searchQueryTemp = input;
        if (input === null && isUnitSearch) {
            this.onSearchSubmit({ query: '*' });
        }
    }

    setSearchScopesByEntity(entity: string) {
        this.searchScopesByEntity = this.searchEntities.filter(
            (s) => s.value === entity,
        )[0].scopes;
        if (this.searchScopesByEntity.length > 0) {
            this.setCurrentScope(this.searchScopesByEntity[0].value);
        }
    }

    setSearchbarPlaceholder(entity: string) {
        this.searchbarPlaceholder = this.searchEntities.filter(
            (s) => s.value === entity,
        )[0].placeholder;
    }

    setCurrentEntity(entity) {
        if (this.activeEntity() !== entity) {
            let itemsPerPage = null;
            switch (entity) {
                case 'researchers':
                    itemsPerPage = Discovery.RESEARCHERS_BULK_SIZE;
                    break;
                default:
                    itemsPerPage = Discovery.ASSETS_BULK_SIZE;
                    break;
            }

            this.clearCheckedItems();
            this.paginationConfig.update((cfg) => ({ ...cfg, itemsPerPage }));
            this.resultCountOptions.set([
                itemsPerPage,
                itemsPerPage * 2,
                itemsPerPage * 3,
            ]);
        }
        this.activeEntity.set(entity);
        this.selectedEntity = entity;
        this.advancedSearchEnabled =
            entity === 'outputs' &&
            this.configurationHandlerService.advancedSearchConfiguration()
                .enableAdvancedSearch;

        const searchEntity = this.searchEntities.filter(
            (s) => s.value === entity,
        )[0];
        if (searchEntity) {
            this.searchbarPlaceholder = searchEntity.placeholder;
            this.showViewToggle = searchEntity.view;
            this.setSearchScopesByEntity(entity);
        }
    }

    setCurrentScope(scope) {
        this.activeScope = scope;
        this.selectedScope = scope;
    }

    handleFilterRemove(item: FacetQuery) {
        const filteredFacets = this.getRemovedFacets(item);
        const filteredMultiFacets = this.getRemovedMultiFacets(item);

        const splicedFacets = (facets) => {
            return facets.filter((facet) => facet !== item.key);
        };

        if (filteredFacets.length > 0) {
            if (
                Array.isArray(this.routeParams.facet) &&
                this.routeParams.facet.length > 1
            ) {
                this.setRoute(
                    {
                        ...this.routeParams,
                        facet: splicedFacets(this.routeParams.facet),
                        page: 1,
                    },
                    this.selectedEntity,
                );
            } else {
                const params = { ...this.routeParams };
                params.page = 1;
                delete params.facet;
                this.setRoute(params, this.selectedEntity);
            }
        } else if (filteredMultiFacets.length > 0) {
            if (
                Array.isArray(this.routeParams.mfacet) &&
                this.routeParams.mfacet.length > 1
            ) {
                this.setRoute(
                    {
                        ...this.routeParams,
                        mfacet: splicedFacets(this.routeParams.mfacet),
                        page: 1,
                    },
                    this.selectedEntity,
                );
            } else {
                const params = { ...this.routeParams };
                params.page = 1;
                delete params.mfacet;
                this.setRoute(params, this.selectedEntity);
            }
        }
    }

    getRemovedFacets(item: FacetQuery) {
        let filteredFacets = [];
        if (this.routeParams.facet) {
            const facets = this.facetService.getFacetsAsArray(
                this.routeParams.facet,
            );
            filteredFacets = facets.filter((facet) => facet === item.key);
        }
        return filteredFacets;
    }

    getRemovedMultiFacets(item: FacetQuery) {
        let filteredMultiFacets = [];
        if (this.routeParams.mfacet) {
            const mfacets = this.facetService.getFacetsAsArray(
                this.routeParams.mfacet,
            );
            filteredMultiFacets = mfacets.filter((facet) => facet === item.key);
        }
        return filteredMultiFacets;
    }

    handleFilterRemoveAll() {
        const params = { ...this.routeParams };
        params.page = 1;
        delete params.facet;
        delete params.mfacet;
        this.setRoute(params, this.selectedEntity);
    }

    scrollToTop() {
        window.scrollTo(0, 0);
    }

    getThumbnail(asset: Asset) {
        if (!asset.thumbnail) {
            return undefined;
        }
        const thumbnail = asset.thumbnail.replace(
            '<inst_code>',
            this.institution,
        );
        return (
            this.configurationHandlerService.externalFacingBaseUrl() + thumbnail
        );
    }

    getItemsRevealLabel(revealed: boolean) {
        return revealed
            ? 'research.results.facets.showLess'
            : 'research.results.facets.showMore';
    }

    doRandomSearch() {
        this.discoveryService
            .getRandomWords()
            .pipe(take(1))
            .subscribe((res) => {
                const random = res as string[];
                this.onSearchSubmit({ query: random.join(' ') });
            });
    }

    getFullAssetPageQueryParams() {
        return { institution: this.institution };
    }

    handleDateFacetChange(values, item, section) {
        item.from = values.from !== undefined ? values.from : item.from;
        item.to = values.to !== undefined ? values.to : item.to;
        section.showSectionActions = true;
    }

    handleDateFacetSubmit(values, section) {
        if (section.items.length > 0) {
            const originalFrom = section.items[0].from
                ? section.items[0].from
                : this.creationDateFacet().initialValues?.min;
            const originalTo = section.items[0].to
                ? section.items[0].to
                : this.creationDateFacet().initialValues?.max;
            const from =
                !values.from || values.from < originalFrom
                    ? originalFrom
                    : values.from;
            let to =
                !values.to || values.to > originalTo ? originalTo : values.to;
            to = Math.abs(to);
            const facet = `${section.key},include,[${from}${Discovery.FACET_DATE_RANGE_SEPERATOR}${to}]`;
            const query = this.addFacetToQuery(facet);
            this.setRoute(query, this.selectedEntity);
        }
    }

    getClearSelectionLabel(key) {
        if (
            key === Discovery.CREATION_DATE_FACET ||
            key === Discovery.INTERNAL_AFFILIATION_START_DATE_FACET
        ) {
            return 'research.results.facets.reset';
        }
        return 'research.results.facets.clear';
    }

    ngOnDestroy() {
        if (!this.componentDestroy.isStopped) {
            this.componentDestroy.next();
            this.componentDestroy.unsubscribe();
        }
    }

    toggleFacetsSidenav() {
        this.sidenavService.toggle('secondary', this.facetsBlock(), {
            position: 'end',
        });
    }

    runAdvancedSearch(params) {
        params['institution'] = this.institution;
        this.setRoute(params, this.selectedEntity);
    }

    goToCollections() {
        this.router.navigate(['/collections'], {
            queryParams: { institution: this.institution },
        });
    }

    organizationsCodes(project: ProjectDetails) {
        return project.extras?.owningDepartments &&
            Object.keys(project.extras.owningDepartments).length
            ? Object.values(project.extras.owningDepartments)
            : [];
    }

    getDateLabel(date: string) {
        return this.systemDateFormatService.getDateLabelFromPartial(date);
    }

    clearCheckedItems() {
        this.checkedItems.set([]);
    }

    updateCheckedItemsByPage(checked: boolean) {
        for (const asset of this.assets()) {
            this.updateCheckedItems(checked, asset);
        }
    }

    updateCheckedItems(checked: boolean, item: any) {
        this.checkedItems.update((items) => {
            const filteredItems = items.filter((i) => i.id !== item.id);
            if (checked) {
                return [...filteredItems, item];
            } else {
                return filteredItems;
            }
        });
    }

    handlePageSizeChange(changeData: MatButtonToggleChange) {
        const newSize = changeData.value;
        this.paginationConfig.update((cfg) => ({
            ...cfg,
            itemsPerPage: newSize,
        }));

        const path = this.route.firstChild?.routeConfig?.path;
        this.performSearch(path, this.routeParams);
    }

    exportAssets(onlySelected: boolean) {
        const dialogClosed = new Subject<null>();
        const xl8 = (key: string | string[], params?: Object) =>
            this.translate.instant(key, params);
        this.exportersService
            .showAssetExportDialog({
                dialogTitle: xl8(
                    'research.portal.fulldisplay.sendTo.export.title',
                ),
                dialogCancel: xl8(
                    'research.portal.fulldisplay.sendTo.export.close',
                ),
                labels: {
                    to: xl8('research.portal.fulldisplay.sendTo.export.to'),
                    copy: xl8('research.portal.fulldisplay.sendTo.export.copy'),
                    copied: xl8(
                        'research.portal.fulldisplay.sendTo.export.copy.success',
                    ),
                },
                selectedAssets: this.checkedItems().map((item) => ({
                    ...item,
                    type: xl8('portal.' + item.resourceType),
                })),
                allAssets: this.allAssets(),
                selectedScope: onlySelected ? 'selected' : 'all',
                exportTargets$: this.assetExportSvc.exportTargets$.pipe(
                    map((exportTargets) =>
                        exportTargets
                            .filter(
                                // remove irrelevant export targets
                                (t) =>
                                    ![
                                        onlySelected &&
                                        this.checkedItems().length === 1
                                            ? null
                                            : 'email',
                                        'print',
                                        'endnote',
                                    ].includes(t.value),
                            )
                            .map(
                                // remove preview for everything but citation
                                (exportTarget) => ({
                                    ...exportTarget,
                                    preview:
                                        exportTarget.value === 'citation'
                                            ? exportTarget.preview
                                            : undefined,
                                }),
                            ),
                    ),
                ),
            })
            .pipe(takeUntil(dialogClosed))
            .subscribe((change) => {
                if (change.event === 'actionClick') {
                    this.assetExportSvc.handleExportActionClick(
                        change,
                        (onlySelected
                            ? this.checkedItems()
                            : this.allAssets()
                        ).map((asset) => asset.id),
                    );
                }
                if (change.event === 'dialogClosed') {
                    this.assetExportSvc.clearOutput();
                    dialogClosed.next(null);
                    dialogClosed.complete();
                }
            });
    }
}
